import React from 'react';
import { buildStatementCoverage } from '../../utils/statementCoverage';
import { monthName } from '../../utils/billCoverage';
import { formatCurrency, formatDate, formatTileAmount } from '../../utils/billFormat';

const MAX_RANGES_SHOWN = 5;

const TILE_CLASS = {
  received: 'bg-[var(--primary)] text-[var(--bg-card)]',
  missing: 'bg-[var(--error-container)] text-[var(--error)]',
  next: 'bg-[var(--bg-hover)] text-[var(--text-secondary)] border border-dashed border-[var(--primary)]',
};

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

const describeCell = (m) => {
  if (m.status === 'received') {
    const extra = m.isDuplicate ? ` ${m.statements.length} statements for this due date.` : '';
    return `${m.longLabel}: statement on file, ${formatCurrency(m.amount)} due.${extra}`;
  }
  if (m.status === 'missing') return `${m.longLabel}: no statement.`;
  if (m.status === 'next') return `${m.longLabel}: next due date.`;
  return undefined;
};

// Phones get a mark; md+ has room for the amount (~48px cells at the narrowest
// desktop layout). The exact values are in each cell's accessible label.
const CellContent = ({ m }) => {
  if (m.status === 'received') {
    return (
      <>
        <span aria-hidden="true" className="md:hidden">
          ✓
        </span>
        <span aria-hidden="true" className="hidden md:inline truncate">
          {formatTileAmount(m.amount) ?? '✓'}
        </span>
      </>
    );
  }
  if (m.status === 'missing') return <span aria-hidden="true">✗</span>;
  if (m.status === 'next') {
    return (
      <span aria-hidden="true" className="truncate">
        <span className="md:hidden">·</span>
        <span className="hidden md:inline">next</span>
      </span>
    );
  }
  return null;
};

const summaryText = (view) => {
  // A loan that hasn't reached its first payment has nothing due yet.
  if (view.expectedCount === 0) {
    return view.nextMonthIndex != null
      ? `First payment due ${monthName(view.nextMonthIndex, 'long')}`
      : 'No due dates yet';
  }
  if (view.missingCount === 0) return `All ${view.expectedCount} due dates on file`;
  return `${view.onFileCount} of ${view.expectedCount} due dates on file · ${view.missingCount} missing`;
};

const rangeLabel = ({ startMonthIndex, endMonthIndex, count }) =>
  startMonthIndex === endMonthIndex
    ? monthName(startMonthIndex, 'short')
    : `${monthName(startMonthIndex, 'short')} – ${monthName(endMonthIndex, 'short')} (${count})`;

/**
 * Every due date since the loan's first payment, one row per year: which have
 * a statement on file and which are missing. All the date rules live in
 * buildStatementCoverage (ADR-0012).
 */
const StatementCoverageGrid = ({ statements, mortgage, today }) => {
  const view = buildStatementCoverage(statements, {
    originationDate: mortgage.origination_date,
    maturityDate: mortgage.maturity_date,
    termMonths: mortgage.term_months,
    today,
  });
  if (!view.years.length) return null;

  const notes = [];
  if (view.source === 'statements') {
    notes.push("Add the loan's origination date to check from its first payment.");
  }
  view.duplicates.forEach(({ monthIndex, statements: sameMonth }) => {
    notes.push(`${monthName(monthIndex, 'long')} has ${sameMonth.length} statements — check for a duplicate.`);
  });
  if (view.beyondSchedule.length) {
    const dates = view.beyondSchedule.map((s) => formatDate(s.due_date)).join(', ');
    // A matured loan has no "next due date" to be past.
    const boundary = view.nextMonthIndex == null ? "the loan's last payment" : 'the next due date';
    notes.push(
      `${plural(view.beyondSchedule.length, 'statement')} ${view.beyondSchedule.length === 1 ? 'is' : 'are'} dated after ${boundary} (${dates}) — check the due date.`,
    );
  }
  if (view.undatedCount) {
    notes.push(`${plural(view.undatedCount, 'statement')} ${view.undatedCount === 1 ? 'has' : 'have'} no due date.`);
  }

  return (
    <div>
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 mb-2">
        <h3 className="text-xs font-semibold uppercase text-[var(--text-secondary)]">Statement coverage</h3>
        <span className="text-xs text-[var(--text-muted)]">
          {summaryText(view)}
        </span>
      </div>

      {/* table-fixed + w-full can never be wider than the card: 12 equal
          columns share what's left after the year column. */}
      <table aria-label="Statement coverage by month" className="w-full table-fixed border-separate border-spacing-0.5">
        <thead>
          <tr>
            <th scope="col" className="w-9">
              <span className="sr-only">Year</span>
            </th>
            {view.years[0].months.map((m) => (
              <th key={m.label} scope="col" className="text-[10px] font-semibold text-[var(--text-muted)]">
                {/* ~18px columns on a phone fit one letter, not "May". Screen
                    readers get the full name at every width. */}
                <span aria-hidden="true" className="md:hidden">
                  {m.label[0]}
                </span>
                <span className="sr-only md:not-sr-only">{m.label}</span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {view.years.map(({ year, months }) => (
            <tr key={year}>
              <th scope="row" className="text-[10px] font-semibold text-[var(--text-secondary)] text-left pr-1">
                {year}
              </th>
              {months.map((m) => {
                const description = describeCell(m);
                return (
                  <td
                    key={m.monthIndex}
                    data-status={m.status}
                    aria-label={description}
                    title={description}
                    className={`relative h-7 min-w-0 rounded text-center text-[10px] leading-none ${TILE_CLASS[m.status] || ''}`}
                  >
                    {m.isDuplicate && (
                      <span
                        aria-hidden="true"
                        className="absolute top-0.5 right-0.5 w-1.5 h-1.5 rounded-full bg-[var(--error)] ring-1 ring-[var(--bg-card)]"
                      />
                    )}
                    <CellContent m={m} />
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-[10px] text-[var(--text-muted)]">
        <span className="inline-flex items-center gap-1">
          <span className="inline-block w-2.5 h-2.5 rounded-sm bg-[var(--primary)]" /> On file
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="inline-block w-2.5 h-2.5 rounded-sm bg-[var(--error-container)]" /> Missing
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="inline-block w-2.5 h-2.5 rounded-sm border border-dashed border-[var(--primary)] bg-[var(--bg-hover)]" />
          Next due
        </span>
      </div>

      {(view.missingRanges.length > 0 || notes.length > 0) && (
        <div className="mt-3 p-3 rounded-lg bg-[var(--bg-hover)] border border-[var(--border)] text-xs text-[var(--text-secondary)] space-y-1">
          {view.missingRanges.length > 0 && (
            <p>
              <span className="font-semibold text-[var(--text-main)]">Missing: </span>
              {view.missingRanges.slice(0, MAX_RANGES_SHOWN).map(rangeLabel).join(' · ')}
              {view.missingRanges.length > MAX_RANGES_SHOWN &&
                ` · and ${view.missingRanges.length - MAX_RANGES_SHOWN} more`}
            </p>
          )}
          {notes.map((note) => (
            <p key={note}>{note}</p>
          ))}
        </div>
      )}
    </div>
  );
};

export default StatementCoverageGrid;
